/**
 * AI Recruit - Live Interview Public (Candidate) API Service
 * Plain axios client, deliberately NOT the `apiV2` instance — this flow is
 * unauthenticated (the token in the URL is the credential) and must never
 * go through the auth-token/401-refresh/logout interceptors `apiV2` has.
 * Mirrors proctoredInterviewPublicApi.ts's shape/conventions exactly.
 *
 * Field names match the real backend (backend/api/v2/endpoints/live_interview_public.py)
 * verbatim — no camelCase transformation layer anywhere in this app's API layer.
 */

import axios from 'axios'
import { API_V2_URL } from './api'

const publicApi = axios.create({
  baseURL: `${API_V2_URL}/live-interview/public`,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
})

export interface LiveInterviewPublicSession {
  session_id: string
  status: string
  recording_chunk_interval_seconds: number
  // How long the room should keep retrying its signaling WebSocket after a
  // drop before giving up. Server-configured
  // (LIVE_INTERVIEW_RECONNECT_TIMEOUT_SECONDS) and carried here rather than on
  // a new endpoint, exactly like recording_chunk_interval_seconds above.
  reconnect_timeout_seconds: number
  // Server-configured (LIVE_TRANSCRIPTION_MAX_LIVE_SEGMENTS) — see
  // useLiveTranscription.ts's own usage for why this rides along here.
  max_live_transcript_segments: number
  // Shown on PreJoinPage's consent screen — null if the underlying
  // job_match/resume/job has since been deleted.
  candidate_name: string | null
  job_title: string | null
  // "human" (a recruiter conducts the call, as normal) or "ai" (the AI
  // interviewer conducts the whole call — see LiveInterviewRoomPage's AI-mode
  // branch and services/live_interview/ai_interviewer.py).
  interview_mode: 'human' | 'ai'
}

export const getSession = (token: string) =>
  publicApi.get<LiveInterviewPublicSession>(`/${token}`).then((r) => r.data)

// PreJoinPage's consent screen — recorded when the candidate ticks the
// checkbox and clicks Continue, mirroring proctoredInterviewPublicApi's
// verifyCandidate.
export const confirmConsent = (token: string) =>
  publicApi.post(`/${token}/consent`).then((r) => r.data)

// PreJoinPage's ID Verification screen — the face+ID photo, sent right
// before the candidate joins the room. Best-effort on the caller's side: a
// failed call must not strand the candidate outside their own interview.
export const submitIdVerification = (token: string, cameraSnapshot: string) =>
  publicApi.post(`/${token}/id-verification`, { camera_snapshot: cameraSnapshot }).then((r) => r.data)

// ---------------------------------------------------------------------------
// Phase C, Part 0: token-gated TURN/STUN credentials for the candidate side
// of the WebRTC peer connection (the authenticated equivalent,
// getTurnCredentials in liveInterviewApi.ts, is recruiter-only).
// ---------------------------------------------------------------------------
export interface IceServer {
  urls: string
  username?: string
  credential?: string
}

export const getPublicTurnCredentials = (token: string): Promise<IceServer[]> =>
  publicApi.get<IceServer[]>(`/${token}/turn-credentials`).then((r) => r.data)

export interface SpeechToken {
  token: string
  // See liveInterviewApi.ts's SpeechToken — real-time recognition is a
  // region-only capability for this resource.
  region: string
  expires_in_seconds: number
  // Server-configured reconnect/refresh tuning — see liveInterviewApi.ts's
  // SpeechToken for why these ride along here instead of being hardcoded.
  token_refresh_seconds: number
  reconnect_burst_base_delay_ms: number
  reconnect_burst_max_attempts: number
  long_retry_interval_seconds: number
  // AI interviewer voice (Azure Neural TTS) — server-configured
  // (AI_INTERVIEWER_TTS_*), see hooks/useAiInterviewerVoice.ts.
  tts_voice?: string
  tts_style?: string
  tts_rate?: string
}

// Candidate-side twin of liveInterviewApi.ts's getSpeechToken — see
// hooks/useLiveTranscription.ts.
export const getPublicSpeechToken = (token: string): Promise<SpeechToken> =>
  publicApi.get<SpeechToken>(`/${token}/speech-token`).then((r) => r.data)

// ---------------------------------------------------------------------------
// Phase C: candidate-submitted answer (code/whiteboard/text) — the recruiter
// -facing "OK" button instead goes through liveInterviewApi.ts's
// authenticated submitAnswer, since the recruiter has a bearer token and the
// candidate does not.
// ---------------------------------------------------------------------------
export interface SubmitPublicAnswerPayload {
  modality: 'audio' | 'text' | 'code' | 'whiteboard'
  transcript?: string | null
  text_content?: string | null
  code_content?: string | null
  code_language?: string | null
  whiteboard_image_data?: string | null
  /**
   * A verbal answer's recorded audio, as BARE base64 — no `data:` URL prefix,
   * which is what the server's b64decode expects (see blobToBase64 in
   * LiveInterviewRoomPage). The server transcribes this with Whisper and
   * scores the resulting text, so `transcript` is left unset when this is
   * present: sending both would be sending two different answers.
   */
  audio_base64?: string | null
  /** Container format of `audio_base64` (`webm`, `ogg`, …) — Whisper
   *  dispatches on the file extension, so the real one has to travel with the
   *  clip rather than being assumed. */
  audio_format?: string
  /**
   * Coding questions only — every Run / Run Test Cases attempt made while
   * working on this answer, accumulated client-side (LiveInterviewRoomPage's
   * wrapped runCodeFn/runTestsFn) since /run and /run-tests are themselves
   * stateless. Same shape as PublicRunCodeResult/PublicRunTestsResult below,
   * tagged with which action produced each entry.
   */
  run_history?: Array<{
    action: 'run' | 'run_tests'
    at: string
    code: string
    stdout?: string
    stderr?: string
    exit_code?: number | null
    results?: PublicTestCaseRunResult[]
  }>
}

export interface PublicAnswerResult {
  id: string
  rubric_score?: number | null
  // AI-interviewer-mode only — see AiInterviewerResponse below. Always
  // undefined for a "human" mode session.
  ai_utterance?: string | null
  ai_action?: 'reveal_question' | 'end_interview' | 'force_submit' | null
  ai_question?: AiInterviewerQuestion | null
}

export const submitPublicAnswer = (token: string, questionId: string, payload: SubmitPublicAnswerPayload) =>
  publicApi.post<PublicAnswerResult>(`/${token}/questions/${questionId}/answer`, payload).then((r) => r.data)

// ---------------------------------------------------------------------------
// AI interviewer (Phase F) — candidate-facing only; an "ai" mode session has
// no recruiter browser to drive anything from, so the candidate's own room
// calls these directly. See backend/services/live_interview/ai_interviewer.py.
// ---------------------------------------------------------------------------

export interface AiInterviewerQuestion {
  id: string
  question_text: string
  skill_name?: string | null
  question_type_id?: string | null
  response_mode?: string | null
  category?: string | null
  test_cases?: { input: string; expected_output: string; description?: string }[]
  duration_minutes?: number | null
}

export interface AiInterviewerResponse {
  utterance: string
  phase?: string | null
  // 'force_submit': the candidate indicated they don't know/want to skip
  // the current question - the frontend is expected to force-submit
  // whatever answer content currently exists (possibly nothing) through its
  // normal submit path, same as a recruiter's manual "Move Forward" in
  // human mode, which is what actually advances the interview.
  action?: 'reveal_question' | 'end_interview' | 'force_submit' | null
  question?: AiInterviewerQuestion | null
  // With action 'end_interview': submit the current answer first, then call
  // endAiInterviewer — the WHOLE interview ends (same as the recruiter's
  // "End Interview"), never a move to the next question.
  submit_current?: boolean | null
  // True when this "turn" was actually the candidate's own mic picking up
  // the AI's own synthesized voice, not anything the candidate said — the
  // backend detected and dropped it as a no-op (see ai_interviewer.py's
  // _is_echo_of_ai). Callers must treat this as if nothing happened at all:
  // no speech, no caption update, no mic re-suppression.
  no_op?: boolean | null
}

// Called once, the instant the candidate's own camera/mic are ready — there's
// no recruiter to wait for, so the AI greets them right away.
export const startAiInterviewer = (token: string) =>
  publicApi.post<AiInterviewerResponse>(`/${token}/ai-interviewer/start`).then((r) => r.data)

// One candidate utterance (already transcribed client-side, via the same
// real-time transcription pipeline the transcript panel uses) in, the AI's
// reaction out.
export const sendAiInterviewerTurn = (token: string, text: string) =>
  publicApi.post<AiInterviewerResponse>(`/${token}/ai-interviewer/turn`, { text }).then((r) => r.data)

// Manual fallback for the candidate-side "Start Interview" button — used when
// the conversational path gets stuck and the AI keeps failing to pick up on
// the candidate asking to begin. Bypasses the AI's own judgment entirely.
export const forceStartAiInterviewer = (token: string) =>
  publicApi.post<AiInterviewerResponse>(`/${token}/ai-interviewer/force-start`).then((r) => r.data)

// Polled by the candidate's room: every queued recruiter-side command (start
// / move forward / end), in the same shape a normal turn returns. Consumed
// on read.
export const endAiInterviewer = (token: string) =>
  publicApi.post<AiInterviewerResponse>(`/${token}/ai-interviewer/end`).then((r) => r.data)

export const pollAiInterviewerEvents = (token: string) =>
  publicApi.post<{ events: AiInterviewerResponse[] }>(`/${token}/ai-interviewer/events`).then((r) => r.data.events)

// ---------------------------------------------------------------------------
// Run / Run Test Cases for coding questions. Same request/response shapes as
// proctoredInterviewPublicApi's runCode/runTestCases — both backends call the
// same services/code_execution.py (real Python subprocess) and
// services/proctored_interview_llm.py (LLM-simulated for every other
// language) — but against Live Interview's own token-gated router, since the
// two products have separate access tokens and a Live Interview token posted
// to /interview/public/... would 404.
//
// Unlike the proctored pair, these are STATELESS server-side: no answer row
// is created, no run history is persisted, and there is no run-count cap (see
// live_interview_public.py). So there is deliberately no run_history_count /
// run_history_limit on these responses; the shared CodeEditor treats both as
// optional and simply omits its attempt counter.
// ---------------------------------------------------------------------------

export interface PublicRunCodeResult {
  stdout: string
  stderr: string
  exit_code?: number | null
  error?: string | null
}

export interface PublicTestCaseRunResult {
  description?: string
  passed: boolean
  explanation?: string
}

export interface PublicRunTestsResult {
  results: PublicTestCaseRunResult[]
}

export const runPublicCode = (
  token: string, questionId: string, code: string, language: string, stdin: string = ''
) =>
  publicApi
    .post<PublicRunCodeResult>(`/${token}/questions/${questionId}/run`, { code, language, stdin })
    .then((r) => r.data)

// `stdin` is intentionally not sent: each of the question's own stored test
// cases supplies its own input, and the backend ignores the field here.
export const runPublicTestCases = (token: string, questionId: string, code: string, language: string) =>
  publicApi
    .post<PublicRunTestsResult>(`/${token}/questions/${questionId}/run-tests`, { code, language })
    .then((r) => r.data)

// Client-side pre-join / in-meeting integrity signals — see PreJoinPage for
// the four checks that log these (camera_mic_granted, permission_denied,
// multi_screen_detected, geolocation_captured, geolocation_denied,
// integrity_monitor_armed, tab_hidden).
export const recordIntegrityEvent = (token: string, eventType: string, eventMetadata?: Record<string, any>) =>
  publicApi
    .post(`/${token}/integrity-events`, { event_type: eventType, event_metadata: eventMetadata ?? null })
    .then((r) => r.data)

// Composited-recording chunk upload — used by useLiveInterviewRecorder. Kept
// as a named function here (rather than exporting `publicApi` itself) so this
// file stays the single place that knows this flow's base URL/headers; the
// backend endpoint (Task 21, cross-cutting) does not exist yet, so calls to
// this will 404 until then — expected, not a bug in the recorder hook.
export const postRecordingChunk = (token: string, index: number, chunk: Blob) =>
  publicApi
    .post(`/${token}/recording-chunks?index=${index}`, chunk, {
      headers: { 'Content-Type': 'application/octet-stream' },
    })
    .then((r) => r.data)

// ---------------------------------------------------------------------------
// Phase D, Part 3: in-call chat — public, token-gated twin of
// liveInterviewApi.ts's authenticated chat endpoints. Sending a message goes
// over the signaling WebSocket (see useLiveInterviewCall), same as the
// recruiter side; these two only ever read history / ack a message as seen.
// ---------------------------------------------------------------------------

export interface LiveInterviewChatMessage {
  id: string
  participant_id: string
  sender_role?: string | null
  text: string
  sent_at?: string | null
  seen_by: string[]
}

export const getPublicChatMessages = (token: string): Promise<LiveInterviewChatMessage[]> =>
  publicApi.get<LiveInterviewChatMessage[]>(`/${token}/chat`).then((r) => r.data)

export const markPublicChatSeen = (token: string, messageId: string): Promise<void> =>
  publicApi.post(`/${token}/chat/${messageId}/seen`).then(() => undefined)

// ---------------------------------------------------------------------------
// Timestamp-Linked Feedback Annotations: transcript history — public,
// token-gated twin of liveInterviewApi.ts's getTranscript. Backlog only,
// same division of labor as chat above: live lines arrive over the
// signaling WebSocket's `transcript-segment` type (see
// hooks/useLiveTranscription.ts).
// ---------------------------------------------------------------------------

export interface LiveInterviewTranscriptSegment {
  id: string
  participant_id: string
  role: 'recruiter' | 'candidate'
  start_seconds: number
  end_seconds: number
  text: string
  created_at?: string | null
}

export const getPublicTranscript = (token: string): Promise<LiveInterviewTranscriptSegment[]> =>
  publicApi.get<LiveInterviewTranscriptSegment[]>(`/${token}/transcript`).then((r) => r.data)

// ---------------------------------------------------------------------------
// Candidate's optional feedback on the assessment EXPERIENCE (platform,
// instructions, timing — never their own interview answers), left on the
// "interview has ended" screen. Mirrors proctoredInterviewPublicApi.ts's own
// FeedbackPayload/submitFeedback exactly.
// ---------------------------------------------------------------------------

export interface LiveInterviewFeedbackPayload {
  rating: number
  tags: string[]
  comment?: string
}

export const submitFeedback = (token: string, feedback: LiveInterviewFeedbackPayload): Promise<{ status: string }> =>
  publicApi.post<{ status: string }>(`/${token}/feedback`, feedback).then((r) => r.data)
